import noop from 'lodash/noop'
import PropTypes from 'prop-types'
import React, { createContext, useContext, useCallback, useState } from 'react'
import { shallowEqual, useSelector, useDispatch } from 'react-redux'

import { selectIsRouteManagerAutoPollingEnabled } from 'data/tenant/tenantSettingsSelectors'
import { setPollingState } from 'slices/route/routeSlice'

// eslint-disable-next-line hh/comment-budget -- load-bearing: the no-op defaults type handlePausePolling as zero-arg and reject every real call
/**
 * @type {React.Context<{
 *   isFeatureEnabled: boolean,
 *   isPollingTemporaryPaused: boolean,
 *   handlePausePolling: (resetLastRefetchTime?: boolean) => void,
 *   handleResumePolling: () => void,
 * }>}
 */
export const RouteManagerApiPollingContext = createContext({
  isFeatureEnabled: false,
  isPollingTemporaryPaused: false,
  handlePausePolling: noop,
  handleResumePolling: noop,
})

export const RouteManagerApiPollingProvider = ({ children }) => {
  const dispatch = useDispatch()
  const [isPollingTemporaryPaused, setIsPollingTemporaryPaused] = useState(false)
  const isFeatureEnabled = useSelector(selectIsRouteManagerAutoPollingEnabled, shallowEqual)

  const handleResetLastRefetchTime = useCallback(() => {
    dispatch(setPollingState({ lastRefetchTime: null }))
  }, [])

  // resetLastRefetchTime - only needed where action fetches the new data, ex. change service date
  const handlePausePolling = useCallback(
    (resetLastRefetchTime = false) => {
      if (!isFeatureEnabled) return

      setIsPollingTemporaryPaused(true)
      if (resetLastRefetchTime) {
        handleResetLastRefetchTime()
      }
    },
    [isFeatureEnabled]
  )

  const handleResumePolling = useCallback(() => {
    if (!isFeatureEnabled) return

    setIsPollingTemporaryPaused(false)
  }, [isFeatureEnabled])

  return (
    <RouteManagerApiPollingContext.Provider
      value={{
        isFeatureEnabled,
        isPollingTemporaryPaused,
        handlePausePolling,
        handleResumePolling,
      }}
    >
      {children}
    </RouteManagerApiPollingContext.Provider>
  )
}

RouteManagerApiPollingProvider.propTypes = {
  children: PropTypes.node,
}

export default RouteManagerApiPollingProvider

export const useRouteManagerApiPollingValue = () => useContext(RouteManagerApiPollingContext)
